import React, { useCallback, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isObject from 'lodash/isObject';
import { useDropzone } from 'react-dropzone';
import { Modal, Row, Col } from 'react-bootstrap';

// GLOBAL VARIABLES
import { UPLOAD } from 'defaults.js';

// GLOBAL FUNCTIONS
import { doCallback } from 'functions.js';

// HELPERS
import useUploadFiles from '../helpers/useUploadFiles';

// CORE COMPONENTS
import Alert from 'core/tools/alert/Alert';
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import UploadStatus from './UploadStatus';

// STYLES
import styles from './uploadModal.module.scss';

// MAIN COMPONENT
const UploadModal = ({
  accept = UPLOAD.accept,
  endpoints = UPLOAD.endpoints || {},
  endpoint = endpoints.upload,
  formatParams,
  label,
  modal: {
    message
  } = {},
  multiple,
  name,
  onClose,
  onComplete,
  onUpload,
  show,
  uploadArgs = {},
  uploadFieldValue,
  uploadMeta,
  // FORMIK BAG
  form: { setFieldTouched } = {}
}) => {
  // STATE
  const [showProgress, setShowProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { uploadFiles, percentComplete } = useUploadFiles({
    endpoint, //
    formatParams,
    onComplete,
    onUpload,
    setShowProgress,
    uploadArgs,
    uploadFieldValue,
    uploadMeta
  });

  // DROPZONE
  const handleDrop = useCallback(
    (accepted = [], rejected = []) => {
      setErrorMessage(null);
  
      const invalidFiles = accepted.filter(
        file => !Object.values(accept).includes(file.type)
      );
      if (invalidFiles.length > 0) {
        setErrorMessage(
          <div className={styles.errors}>
            <h2 className="h4 mb-3 font-weight-bolder">Upload Failed</h2>
            <ul className="ps-4">
              {invalidFiles.map((file, i) => (
                <li key={file.name} className={clsx(i < invalidFiles.length - 1 && 'mb-2')}>
                  <strong className="d-block mb-2">{file.name}</strong>
                  <span className="d-block mb-2">
                    This file type is not supported. The following file types are supported: {Object.keys(accept)
                      .map(k => `.${k}`)
                      .join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
        return;
      }
  
      if (!multiple && (accepted || []).length > 1) {
        setErrorMessage(
          <div className={styles.errors}>
            <p>For this document, only 1 file is allowed. Please choose only 1 file.</p>
          </div>
        );
        return;
      }
  
      if (rejected.length) {
        setErrorMessage(
          <div className={styles.errors}>
            <h2 className="h4 mb-3 fw-bolder">Upload Failed</h2>
            <ul className="ps-4">
              {rejected.map(({ file, errors = [] }, i) => (
                <li key={file.name} className={clsx(i < rejected.length - 1 && 'mb-2')}>
                  <strong className="d-block mb-2">{file.name}</strong>
                  {errors?.map((e, j) => (
                    <span key={e.code} className={clsx('d-block', j < errors?.length - 1 && 'mb-2')}>
                      {e.message}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        );
        return;
      }
      doCallback(setFieldTouched, name, true);
      onClose();
      uploadFiles(accepted);
    },
    [multiple, name, onClose, setFieldTouched, uploadFiles, accept]
  );
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: async (a, r) => handleDrop(a, r),
  });

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      open(e);
    },
    [open]
  );

  // RENDER
  return (
    <>
      <Modal
        size="lg"
        aria-labelledby="upload-modal"
        centered
        scrollable
        dialogClassName={styles.dialog}
        contentClassName="overflow-auto"
        backdropClassName="bg-black"
        show={show}
        onHide={onClose}
        backdrop="static"
      >
        <div className="p-3">
          <Modal.Header className="flex-column">
            {label && (
              <Modal.Title as="h3" className="mx-auto fw-bolder text-primary">
                {isObject(label) ? label.children : label}
              </Modal.Title>
            )}
          </Modal.Header>
          <Modal.Body>
            {!!message && <div className="mb-4">{message}</div>}
            {errorMessage && (
              <Alert.Dismissable
                variant="danger" //
                className="w-100 mb-2"
                message={errorMessage}
                onClose={() => setErrorMessage(null)}
              />
            )}
            <div
              {...getRootProps()}
              id="upload-area"
              className={clsx(styles.container, isDragActive && styles.active, 'text-primary text-center')}
            >
              <input
                {...getInputProps({
                  multiple,
                  accept
                })}
              />
              <Button
                variant="light"
                label="Choose File"
                onClick={handleClick}
                className="mx-auto text-black"
                fullWidth={false}
              />
              <p className="mt-3 large text-black">Or Drag and Drop Here</p>
            </div>
          </Modal.Body>
          <Modal.Footer className={styles.footer}>
            <div className="w-100">
              <Row className="justify-content-center">
                <Col xs={24} sm={16} lg={12}>
                  <Button.Cancel onClick={onClose} />
                </Col>
              </Row>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
      <UploadStatus show={showProgress} percentComplete={percentComplete} onClose={() => setShowProgress(false)} />
    </>
  );
};

export default UploadModal;